import { differenceInCalendarDaysUTC, endOfDayUTC, startOfDayUTC } from "~/utils/datetime"
import { type FiscalConfig, type FiscalYearPeriod, fiscalYearRange } from "~/utils/fiscalCalendar"
import { getStoredFiscalConfig } from "~/stores/fiscal-config-store"

/**
 * Day-proportional share of `amount` (spread evenly over startDate–endDate) that falls in FY`year`,
 * whose bounds come from the company's fiscal calendar (`fiscalYearRange`), or the calendar year
 * without one. Server callers must pass `fiscalConfig`: the default reads the client-only store.
 */
export function calculateTimeBasedAllocation({
  amount,
  startDate,
  endDate,
  year,
  fiscalConfig = getStoredFiscalConfig(),
}: {
  amount: number
  startDate: Date | null
  endDate: Date | null
  year: number
  fiscalConfig?: FiscalConfig | null
}): number {
  if (!startDate || !endDate) return 0
  const range = fiscalYearRange(year, fiscalConfig)
  return calculateBudgetAllocationForRange(
    { amount, startDate, endDate },
    range.startDate,
    range.endDate,
  )
}

/**
 * Calculate budget allocation for a specific year using time-based proportional allocation.
 *
 * @param budget - The budget object containing amount, start date, and end date
 * @param year - The year for which to calculate the allocation (e.g., 2024)
 * @returns The allocated budget amount for the specified year
 */
export function calculateBudgetAllocationForYear(
  budget: { amount: number; startDate: Date; endDate: Date },
  year: number,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): number {
  return calculateTimeBasedAllocation({
    amount: budget.amount,
    startDate: budget.startDate,
    endDate: budget.endDate,
    year,
    fiscalConfig,
  })
}

/**
 * Portion of a budget's amount that falls within an arbitrary date range, by day-based
 * proportion. Used to attribute a budget to a fiscal-year window (and to split a budget
 * that straddles two fiscal years). Each day counts the same, so a boundary budget splits
 * by calendar days, not demand shape.
 */
export function calculateBudgetAllocationForRange(
  budget: { amount: number; startDate: Date; endDate: Date },
  rangeStart: Date,
  rangeEnd: Date,
): number {
  if (budget.amount === 0) return 0

  const periodStart = startOfDayUTC(budget.startDate)
  const periodEnd = endOfDayUTC(budget.endDate)
  const overlapStart = new Date(
    Math.max(periodStart.getTime(), startOfDayUTC(rangeStart).getTime()),
  )
  const overlapEnd = new Date(Math.min(periodEnd.getTime(), endOfDayUTC(rangeEnd).getTime()))

  if (overlapStart.getTime() > overlapEnd.getTime()) return 0

  const totalPeriodDays = differenceInCalendarDaysUTC(periodEnd, periodStart) + 1
  const overlapDays = differenceInCalendarDaysUTC(overlapEnd, overlapStart) + 1
  return (budget.amount * overlapDays) / totalPeriodDays
}

/**
 * Target minus allocated, in whole units. Allocation is day-prorated (fractional) but targets are
 * entered in whole units, so a sub-unit gap is balanced rather than over- or under-allocated.
 */
export function calculateUnallocated(target: number, allocated: number): number {
  const rounded = Math.round(target - allocated)
  // Math.round(-0.4) is -0, which formats as "-0".
  return rounded === 0 ? 0 : rounded
}

type AllocatedVariant = "green" | "yellow" | "red"

const allocatedVariantFor = (pct: number, overAllocated: boolean): AllocatedVariant => {
  if (overAllocated) return "red"
  if (pct >= 90) return "green"
  if (pct >= 60) return "yellow"
  return "red"
}

/**
 * Allocated budget as a whole percentage of the target, with its badge colour. Over-allocated
 * (past 100%, or a negative `calculateUnallocated` gap) is red, so it agrees with the Unallocated
 * card even when the percentage rounds to 100. Null without a positive target.
 */
export function allocatedOfTarget(
  target: number | null,
  allocated: number,
): { pct: number; variant: AllocatedVariant; unallocated: number; overAllocated: boolean } | null {
  if (target == null || target <= 0) return null
  const pct = Math.round((allocated / target) * 100)
  const unallocated = calculateUnallocated(target, allocated)
  const overAllocated = pct > 100 || unallocated < 0
  return { pct, variant: allocatedVariantFor(pct, overAllocated), unallocated, overAllocated }
}

/**
 * Calculate forecast allocation for a specific year using time-based proportional allocation.
 *
 * TODO: This currently uses the same days calculation as budget allocation, but this will change in the future
 * to use more sophisticated forecast distribution logic based on collection-specific forecasting models.
 *
 * @param collection - The collection object containing forecast and date information
 * @param year - The year for which to calculate the allocation (e.g., 2024)
 * @returns The allocated forecast amount for the specified year
 */
export function calculateForecastAllocationForYear(
  collection: { forecastedUnits: number; startDate: Date | null; endDate: Date | null },
  year: number,
): number {
  return calculateTimeBasedAllocation({
    amount: collection.forecastedUnits,
    startDate: collection.startDate,
    endDate: collection.endDate,
    year,
  })
}

/**
 * Calculate forecasted revenue allocation for a specific year.
 *
 * This function calculates the forecasted revenue by:
 * 1. Calculating the forecasted units for the year using time-based allocation
 * 2. Determining the average unit price from historical sales data
 * 3. Multiplying forecasted units by average price to get forecasted revenue
 *
 * @param collection - The collection object containing forecast, sales, and date information
 * @param year - The year for which to calculate the revenue allocation (e.g., 2024)
 * @returns The allocated forecasted revenue for the specified year
 */
export function calculateForecastRevenueAllocationForYear(
  collection: {
    forecastedUnits: number
    salesQuantity: number
    salesRevenue: number
    startDate: Date | null
    endDate: Date | null
  },
  year: number,
): number {
  const forecastForYear = calculateForecastAllocationForYear(collection, year)
  const avgPrice =
    collection.salesQuantity > 0 ? collection.salesRevenue / collection.salesQuantity : 0
  return forecastForYear * avgPrice
}

/** Each period's total of the budgets' day-proportional amounts (periods from `fiscalYearPeriods`). */
export function getPeriodBudgetDistribution(
  budgets: Array<{ amount: number; startDate: Date; endDate: Date }>,
  periods: FiscalYearPeriod[],
): number[] {
  return periods.map((period) =>
    budgets.reduce(
      (sum, budget) => sum + calculateBudgetAllocationForRange(budget, period.start, period.end),
      0,
    ),
  )
}
